'use client';

import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { useRequestOTP } from '@/api/use-request-otp';
import { trackEvent } from '@/lib/event-tracking';
import { useAuth } from '@/providers/auth-provider';
import { useAuthStore } from '@/store/use-auth-store';

type AuthStep = 'phone' | 'otp' | 'success';

export function useLoginDialog() {
  const { login, isLoading: isFBLoading } = useAuth();
  const { isAuthenticated } = useAuthStore();

  const [currentStep, setCurrentStep] = useState<AuthStep>('phone');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string>('');

  const { mutate: requestOTP, isPending: isRequestingOTP } = useRequestOTP({
    onSuccess: () => {
      setCurrentStep('otp');
      setPhoneError('');
    },
    onError: (error: any) => {
      const errorData = error.response?.data || {};
      const errorTitle = errorData.error || errorData.title;

      if (errorTitle === 'ACTIVE_OTP_EXISTS') {
        setCurrentStep('otp');
        toast.warning(
          'Bạn đã yêu cầu OTP trước đó. Vui lòng kiểm tra tin nhắn SMS.'
        );
      } else if (errorTitle === 'TOO_MANY_REQUESTS') {
        setPhoneError(
          errorData.message ||
            'Bạn đã vượt quá số lần yêu cầu. Vui lòng thử lại sau.'
        );
      } else {
        setPhoneError(
          errorData.message || error.message || 'Đã xảy ra lỗi khi gửi OTP'
        );
      }
    },
  });

  const handlePhoneSuccess = useCallback(
    (phone: string) => {
      setPhoneNumber(phone);
      requestOTP({ phone_number: phone });
    },
    [requestOTP]
  );

  const handleOTPSuccess = useCallback(() => {
    setCurrentStep('success');
  }, []);

  const handleBackToPhone = useCallback(() => {
    setCurrentStep('phone');
    setPhoneNumber('');
    setPhoneError('');
  }, []);

  const handleFacebookLogin = useCallback(async () => {
    trackEvent('check_fb_log_in', {
      action_time: Math.floor(Date.now() / 1000),
      is_login: isAuthenticated ? 'true' : 'false',
      event_type: 'button_click',
      event_date: new Date().toISOString().split('T')[0],
      event_timestamp: Math.floor(Date.now() / 1000),
      button_name: 'login_facebook',
      button_location: 'unknown',
    });

    try {
      await login();
    } catch (error) {
      console.error('Facebook login failed:', error);
      toast.error('Đăng nhập Facebook thất bại. Vui lòng thử lại.');
    }
  }, [login, isAuthenticated]);

  const resetState = useCallback(() => {
    setCurrentStep('phone');
    setPhoneNumber('');
    setPhoneError('');
  }, []);

  const handleClose = useCallback(() => {
    if (!isFBLoading && !isRequestingOTP) {
      resetState();
    }
  }, [isFBLoading, isRequestingOTP, resetState]);

  const handleSuccess = useCallback(() => {
    resetState();
  }, [resetState]);

  return {
    currentStep,
    phoneNumber,
    phoneError,
    isRequestingOTP,
    isFBLoading,
    handlePhoneSuccess,
    handleOTPSuccess,
    handleBackToPhone,
    handleFacebookLogin,
    handleClose,
    handleSuccess,
    setPhoneError,
  };
}
